import { db } from './Database';

export interface StackLeanDetail {
    id?: number;
    palletLpnNumberId: number;
    variant: string;
    weekType: string | null;
    sideABottom: string;
    sideATop: string;
    sideALean?: string;
    sideBBottom: string;
    sideBTop: string;
    sideBLean?: string;
    sideCBottom: string;
    sideCTop: string;
    sideCLean?: string;
    sideDBottom: string;
    sideDTop: string;
    sideDLean?: string;
    createdAt?: string;
    updatedAt?: string;
}

export class StackLeanService {
    // Get stack lean details for a pallet, variant and weekType
    static async getStackLeanDetails(palletId: number, variant: string, weekType: string): Promise<{ palletLpnNumbers: any[] }> {
        try {
            // First get all LPN numbers for the pallet
            const palletLpnNumbers = await db.palletLpnNumbers
                .where('palletId')
                .equals(palletId)
                .toArray();

            if (!palletLpnNumbers || palletLpnNumbers.length === 0) {
                throw new Error('Pallet LPN Numbers not found');
            }

            // Get lean details for each LPN number with specific week
            const response = await Promise.all(palletLpnNumbers.map(async palletLpnNumber => {
                const leanDetails = await db.leans
                    .where('palletLpnNumberId')
                    .equals(palletLpnNumber.id!)
                    .filter(lean => lean.variant === variant && lean.weekType === weekType)
                    .toArray();

                return {
                    ...palletLpnNumber,
                    leanDetails: leanDetails || []
                };
            }));

            return {
                palletLpnNumbers: response
            };
        } catch (error) {
            console.error('Error getting stack lean details:', error);
            throw error;
        }
    }

    // Get unique weeks for stack lean
    static async getStackLeanUniqueWeeks(palletId: number, variant: string): Promise<{ weeks: string[] }> {
        try {
            // Get all LPN numbers for the pallet
            const palletLpnNumbers = await db.palletLpnNumbers
                .where('palletId')
                .equals(palletId)
                .toArray();

            if (!palletLpnNumbers || palletLpnNumbers.length === 0) {
                return { weeks: [] };
            }

            // Get all lean details for these LPN numbers
            const uniqueWeeks = new Set<string>();

            for (const lpn of palletLpnNumbers) {
                const leanDetails = await db.leans
                    .where('palletLpnNumberId')
                    .equals(lpn.id!)
                    .filter(lean => lean.variant === variant && lean.weekType !== null)
                    .toArray();

                leanDetails.forEach(lean => {
                    if (lean.weekType) uniqueWeeks.add(lean.weekType);
                });
            }

            // Sort weeks in correct order
            const weekOrder = [
                "Stack start(stacked+30m)",
                "Stack Week-1",
                "Stack Week-2",
                "Stack Week-3",
                "Stack Week-4 (unstacking+30M)"
            ];

            const sortedWeeks = Array.from(uniqueWeeks).sort((a, b) =>
                weekOrder.indexOf(a) - weekOrder.indexOf(b)
            );

            return { weeks: sortedWeeks };
        } catch (error) {
            console.error('Error getting unique weeks:', error);
            throw error;
        }
    }

    // Create or update stack lean details
    static async createStackLeanDetails(data: { palletId: number, palletLpnNumbers: any[] }): Promise<{ palletLpnNumbers: any[] }> {
        const { palletId, palletLpnNumbers } = data;

        return await db.transaction('rw', [db.palletLpnNumbers, db.leans], async () => {
            try {
                const createdOrUpdatedLeanDetails = [];
                const timestamp = new Date().toISOString();

                for (const palletLpnNumber of palletLpnNumbers) {
                    const { id, lpnNo, endStatusPallet, leanDetails, ...palletLpnNumberData } = palletLpnNumber;

                    // Create or update PalletLpnNumber
                    let palletLpnNumberRecord;
                    if (id) {
                        await db.palletLpnNumbers.update(id, {
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(id);
                    } else {
                        const newId = await db.palletLpnNumbers.add({
                            lpnNo,
                            endStatusPallet,
                            palletId,
                            ...palletLpnNumberData,
                            createdAt: timestamp,
                            updatedAt: timestamp
                        });
                        palletLpnNumberRecord = await db.palletLpnNumbers.get(newId);
                    }

                    if (!palletLpnNumberRecord) continue;

                    // Create or update lean details
                    const leanDetailsArray = [];
                    for (const leanDetail of leanDetails) {
                        const { id, ...leanData } = leanDetail;

                        if (id) {
                            await db.leans.update(id, {
                                ...leanData,
                                updatedAt: timestamp
                            });
                            const updated = await db.leans.get(id);
                            if (updated) leanDetailsArray.push(updated);
                        } else {
                            const newId = await db.leans.add({
                                ...leanData,
                                palletLpnNumberId: palletLpnNumberRecord.id,
                                createdAt: timestamp,
                                updatedAt: timestamp
                            });
                            const created = await db.leans.get(newId);
                            if (created) leanDetailsArray.push(created);
                        }
                    }

                    createdOrUpdatedLeanDetails.push({
                        ...palletLpnNumberRecord,
                        leanDetails: leanDetailsArray
                    });
                }

                return {
                    palletLpnNumbers: createdOrUpdatedLeanDetails
                };
            } catch (error) {
                console.error('Error creating/updating stack lean details:', error);
                throw error;
            }
        });
    }
}
